import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "../../../services";

const debounceTimeout = 500;
const minTextLength = 3;

// Fetches the "most searched" suggestions shown under the AI search input.
// Only active in AI mode, and only once the trimmed input reaches 3 characters.
//
// Several SearchBar instances are mounted at once (the in-page bar plus the one
// in the header), and they all read the same searchText/isAiMode from redux, so
// without `isSuggestionOwner` every mounted bar would fire its own identical
// mostSearched request on each keystroke. Only the instance actually presenting
// the search UI passes true.
export const useAiSuggestions = (
  searchText: string,
  isAiMode: boolean,
  isSuggestionOwner: boolean = true
) => {
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [aiSuggestionsLoading, setAiSuggestionsLoading] = useState(false);
  const timeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  // Incremented per request so a slow response for "fia" cannot overwrite a fresh one for "fiat"
  const requestIdRef = useRef(0);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      clearTimeout(timeoutRef.current);
    };
  }, []);

  const clearAiSuggestions = useCallback(() => {
    clearTimeout(timeoutRef.current);
    requestIdRef.current += 1;
    // Keep the same array reference when already empty, so clearing on every
    // keystroke below the 3-character threshold does not churn renders
    setAiSuggestions(prev => (prev.length === 0 ? prev : []));
    setAiSuggestionsLoading(false);
  }, []);

  useEffect(() => {
    const text = searchText ? searchText.trim() : "";

    if (!isAiMode || !isSuggestionOwner || text.length < minTextLength) {
      clearAiSuggestions();
      return;
    }

    setAiSuggestionsLoading(true);
    clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(async () => {
      requestIdRef.current += 1;
      const requestId = requestIdRef.current;

      let suggestions = [];
      try {
        const response = await api.AiSearch.mostSearched(text, () => null);
        if (response && Array.isArray(response.suggestions)) {
          suggestions = response.suggestions;
        }
      } catch (err) {
        // A failed suggestions lookup must never disturb the search bar
        suggestions = [];
      }

      // Ignore stale responses and updates after unmount
      if (!isMountedRef.current || requestId !== requestIdRef.current) return;

      setAiSuggestions(suggestions);
      setAiSuggestionsLoading(false);
    }, debounceTimeout);

    return () => clearTimeout(timeoutRef.current);
  }, [searchText, isAiMode, isSuggestionOwner, clearAiSuggestions]);

  return { aiSuggestions, aiSuggestionsLoading, clearAiSuggestions };
};



// WEBPACK FOOTER //
// ./src/containers/HomePage/hooks/useAiSuggestions.ts